/**
 * EVIDENCE — the artifact library, an artifact's lineage, and the connectors that
 * collect it.
 *
 * Split from DEFINE because sources fail on their own schedule and for their own
 * reasons. When a control goes UNKNOWN the cause is usually here, not in the
 * policy, and the product should route the reviewer accordingly — which is why
 * every UNKNOWN finding in this prototype links to the source that caused it.
 */

import { useMemo, useState } from "react";
import {
  ARTIFACTS,
  NOW,
  HEALTH_TONE,
  PROVIDER_LABEL,
  SOURCES,
  actor,
  artifact as findArtifact,
  control,
  fmtBytes,
  fmtDateTime,
  fmtNumber,
  fmtRelative,
  observationsFor,
  receipt,
  source,
  type EvidenceArtifact,
  type Source,
} from "./data";
import { href } from "./router";
import {
  Badge,
  Button,
  Callout,
  Card,
  Empty,
  MissingObjectPage,
  Field,
  Fields,
  Grid,
  Hash,
  PageShell,
  Section,
  SearchBox,
  Select,
  Stat,
  Table,
  Timeline,
  TimelineItem,
  Verdict,
} from "./ui";
import type { Column } from "./ui";
import { useStore } from "./store";

/* ══════════════════════════════════════════════════════════════════════════
   COPY THAT DESCRIBES SHIPPED BEHAVIOUR

   Same rule as `pages-prove.tsx`: this prototype compiles into the product's
   client bundle and is served at `/styleguide/verification/*`, so sentences
   about what the engine does carry the canonical page's wording, measured in
   3d54a87cb against `excellent/server/verification-evidence.ts`.

   Held as module constants so prettier's printWidth-100 JSX fill cannot re-wrap
   a pinned phrase across a newline.
   ══════════════════════════════════════════════════════════════════════════ */

/** Mirrors `LIBRARY_LEDE`. `handleVerificationEvidenceRedact` rewrites the row
 *  with `bucket.artifacts = bucket.artifacts.map(...)` — editing in place, three
 *  lines under the sentence that used to deny it. */
const LIBRARY_LEDE =
  "Every artifact is recorded with a content hash and an append-only lineage. The hash names the bytes that were observed; it does not itself prove immutability, because this store holds the metadata and not the payload.";

/** Mirrors `REDACT_BLOCKED_NOTE`. */
const REDACT_BLOCKED_NOTE =
  "Recording a redaction is blocked while that is true, so that artifact state does not conflict with existing receipt metadata.";

/** Mirrors `REDACTED_STATE_NOTE` + `REDACTED_REPLAY_NOTE`. Redact sets
 *  `state: "redacted"` and appends a lineage event; it removes nothing, and there
 *  is no payload field on the artifact to remove. Replay refuses
 *  `receipt-integrity-unavailable` without a signed envelope (3518f8a9a). */
const REDACTED_STATE_NOTE =
  "This artifact is marked redacted, and its content hash, size and lineage are retained. That records a deletion decision here and does not establish whether a separate payload was deleted in whatever system holds it.";

const REDACTED_REPLAY_NOTE =
  "For a receipt that cited this artifact, authoritative receipt replay is unavailable unless a signed envelope was retained for it.";

/* ══════════════════════════════════════════════════════════════════════════
   EVIDENCE LIBRARY
   ══════════════════════════════════════════════════════════════════════════ */

export function EvidencePage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const store = useStore();
  const [q, setQ] = useState("");
  const [src, setSrc] = useState("all");
  const [state, setState] = useState<"all" | "active" | "redacted">("all");

  const rows = useMemo(() => {
    let list = ARTIFACTS;
    if (src !== "all") list = list.filter((a) => a.sourceId === src);
    if (state !== "all") list = list.filter((a) => a.state === state);
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter(
        (a) =>
          a.label.toLowerCase().includes(n) ||
          a.sourceKind.toLowerCase().includes(n) ||
          a.contentHash.includes(n),
      );
    }
    return [...list].sort((a, b) => new Date(b.collectedAt).getTime() - new Date(a.collectedAt).getTime());
  }, [q, src, state]);

  const columns: Column<EvidenceArtifact>[] = [
    {
      key: "label",
      header: "Artifact",
      primary: true,
      sortValue: (a) => a.label,
      render: (a) => (
        <>
          {a.label}
          <div className="vp-mono" style={{ color: "var(--ink-3)", marginTop: "calc(var(--spacing) * 0.5)", fontSize: "var(--type-subtext-size)" }}>
            {a.sourceKind}
          </div>
        </>
      ),
    },
    {
      key: "source",
      header: "Source",
      sortValue: (a) => source(a.sourceId)?.displayName ?? "",
      render: (a) => PROVIDER_LABEL[source(a.sourceId)!.provider],
    },
    {
      key: "hash",
      header: "Content hash",
      render: (a) => <Hash value={a.contentHash} onCopy={() => store.toast("Content hash copied.")} />,
    },
    {
      key: "obs",
      header: "Observations",
      sortValue: (a) => a.observationCount,
      render: (a) => <span className="vp-mono">{fmtNumber(a.observationCount)}</span>,
    },
    // Size is on the artifact detail page. Eight columns overflowed a laptop
    // viewport and hid "Cited by" and "Collected", which are the two a reviewer
    // actually scans this table for.
    {
      key: "state",
      header: "State",
      sortValue: (a) => a.state,
      render: (a) => (
        <Badge tone={a.state === "active" ? "pass" : a.state === "redacted" ? "waived" : "neutral"}>
          {a.state}
        </Badge>
      ),
    },
    {
      key: "cited",
      header: "Cited by",
      sortValue: (a) => a.citedByReceiptIds.length,
      render: (a) =>
        a.citedByReceiptIds.length === 0 ? (
          <span className="vp-muted">—</span>
        ) : (
          <Badge tone="accent">
            {a.citedByReceiptIds.length} receipt{a.citedByReceiptIds.length === 1 ? "" : "s"}
          </Badge>
        ),
    },
    {
      key: "collected",
      header: "Collected",
      sortValue: (a) => new Date(a.collectedAt).getTime(),
      render: (a) => <span className="vp-mono">{fmtRelative(a.collectedAt)}</span>,
    },
  ];

  return (
    <PageShell
      wide
      eyebrow="Evidence"
        title="Evidence library"
        lede={LIBRARY_LEDE}
    >

      <Grid cols={4}>
        <Stat
          label="Artifacts"
          value={ARTIFACTS.length}
          foot={`${fmtBytes(ARTIFACTS.reduce((s, a) => s + a.sizeBytes, 0))} total, all encrypted at rest`}
        />
        <Stat
          label="Observations"
          value={fmtNumber(ARTIFACTS.reduce((s, a) => s + a.observationCount, 0))}
          foot="Derived facts a check can read"
        />
        <Stat
          label="Cited by a receipt"
          value={ARTIFACTS.filter((a) => a.citedByReceiptIds.length > 0).length}
          tone="accent"
          foot="These cannot be deleted while the receipt stands"
        />
        <Stat
          label="Redacted"
          value={ARTIFACTS.filter((a) => a.state === "redacted").length}
          foot="Content removed, hash and lineage retained"
        />
      </Grid>

      <div className="vp-filters" style={{ marginTop: "calc(var(--spacing) * 5.5)" }}>
        <SearchBox value={q} onChange={setQ} placeholder="Search artifacts, kinds, hashes…" />
        <Select
          label="Source"
          value={src}
          onChange={setSrc}
          options={[
            { value: "all", label: "All sources" },
            ...SOURCES.map((s) => ({ value: s.id, label: PROVIDER_LABEL[s.provider] })),
          ]}
        />
        <Select
          label="State"
          value={state}
          onChange={setState}
          options={[
            { value: "all", label: "All states" },
            { value: "active", label: "Active" },
            { value: "redacted", label: "Redacted" },
          ]}
        />
        <span className="vp-filter-spacer" />
        <span className="vp-result-count">
          {rows.length} of {ARTIFACTS.length}
        </span>
      </div>

      <Card flush>
        <Table
          columns={columns}
          rows={rows}
          rowKey={(a) => a.id}
          onRowClick={(a) => onNavigate(href("evidence", a.id))}
          empty={<Empty title="No artifacts match">Clear the filters to see the full library.</Empty>}
        />
      </Card>
    </PageShell>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   ARTIFACT DETAIL — lineage
   ══════════════════════════════════════════════════════════════════════════ */

export function ArtifactDetailPage({ id, onNavigate }: { id: string; onNavigate: (to: string) => void }) {
  const store = useStore();
  const a = findArtifact(id);

  if (!a) {
    return (
      <MissingObjectPage title={`No artifact ${id}`}>
        <Button variant="primary" onClick={() => onNavigate(href("evidence"))}>
          Back to the library
        </Button>
      </MissingObjectPage>
    );
  }

  const s = source(a.sourceId);
  const obs = observationsFor(a.id);

  return (
    <PageShell
      wide
      eyebrow={`Evidence artifact · ${a.sourceKind}`}
        title={a.label}
        lede={`Collected ${fmtDateTime(a.collectedAt)} from ${s?.displayName} by ${actor(a.collectorActorId).name}.`}
        actions={
          <>
        <Button
              icon="download"
              onClick={() => store.toast("Prototype: the artifact would download with its manifest.")}
            >
              Download
        </Button>
        <Button
              variant={a.citedByReceiptIds.length > 0 ? "outline" : "reject"}
              icon="x"
              disabled={a.citedByReceiptIds.length > 0}
              title={
                a.citedByReceiptIds.length > 0
                  ? `Cannot redact — cited by ${a.citedByReceiptIds.length} standing receipt(s)`
                  : "Redact this artifact's content"
              }
              onClick={() => store.toast("Prototype: redaction would remove content and retain the hash.")}
            >
              Redact
        </Button>
          </>
        }
    >

      {a.citedByReceiptIds.length > 0 && a.state !== "redacted" ? (
        <Callout tone="accent" icon="lock" title="This artifact is load-bearing.">
          {a.citedByReceiptIds.length} standing receipt
          {a.citedByReceiptIds.length === 1 ? "" : "s"} cite it. {REDACT_BLOCKED_NOTE} Retire the receipt
          first, or let retention expire it.
        </Callout>
      ) : null}

      {a.state === "redacted" ? (
        <div style={{ marginTop: "calc(var(--spacing) * 3)" }}>
          <Callout tone="waived" icon="eye" title="Deletion state recorded; metadata retained">
            {REDACTED_STATE_NOTE} {REDACTED_REPLAY_NOTE}
          </Callout>
        </div>
      ) : null}

      <div className="vp-split" style={{ marginTop: "calc(var(--spacing) * 5)" }}>
        <div className="vp-stack">
          <Card
            title="Observations"
            subtitle="The derived facts a check actually reads. An artifact is raw; an observation is a claim about it."
            flush
          >
            {obs.length === 0 ? (
              <div className="vp-card-body">
                <p className="vp-tiny" style={{ margin: "0" }}>
                  {fmtNumber(a.observationCount)} observations were extracted. This fixture carries a sample
                  rather than the full set.
                </p>
              </div>
            ) : (
              <Table
                columns={[
                  {
                    key: "subject",
                    header: "Subject",
                    primary: true,
                    render: (o) => <span className="vp-mono">{o.subjectRef}</span>,
                  },
                  { key: "pred", header: "Predicate", render: (o) => <span className="vp-mono">{o.predicate}</span> },
                  { key: "value", header: "Value", render: (o) => <span className="vp-mono">{o.value}</span> },
                  {
                    key: "ex",
                    header: "Extractor",
                    render: (o) => <span className="vp-mono" style={{ fontSize: "var(--type-subtext-size)" }}>{o.extractorVersion}</span>,
                  },
                ]}
                rows={obs}
                rowKey={(o) => o.id}
              />
            )}
          </Card>

          <Card
            title="Lineage"
            subtitle="A hash chain. Each event's hash includes the previous one, so an inserted or removed event is detectable."
          >
            <Timeline>
              <TimelineItem
                tone="accent"
                glyph="▸"
                title="Collected"
                meta={`${fmtDateTime(a.collectedAt)} · ${s?.displayName} · read-only connector`}
                note={`Scopes used: ${s?.grantedScopes.join(", ")}. The connector holds no write capability against this source.`}
              />
              <TimelineItem
                tone="accent"
                glyph="⇣"
                title={`Derived ${fmtNumber(a.observationCount)} observations`}
                meta={`Extractor ${obs[0]?.extractorVersion ?? "—"}`}
                note="Extraction is versioned. Re-running a newer extractor appends a new observation version rather than overwriting, so an old verdict stays reproducible."
              />
              {a.citedByReceiptIds.map((rid) => {
                const r = receipt(rid);
                return (
                  <TimelineItem
                    key={rid}
                    tone="pass"
                    glyph="⦿"
                    title={`Cited by receipt ${rid}`}
                    meta={r ? `${fmtDateTime(r.issuedAt)} · verdict ${r.status}` : rid}
                  />
                );
              })}
              {a.state === "redacted" ? (
                <TimelineItem
                  tone="waived"
                  glyph="⦸"
                  title="Redacted"
                  meta="Content removed, hash retained"
                  note="Requested under data minimisation. The chain records that a redaction happened, which is itself part of the audit trail."
                />
              ) : null}
            </Timeline>
          </Card>
        </div>

        <aside className="vp-split-aside">
          <Card title="Artifact record">
            <Fields>
              <Field label="Artifact id">
                <span className="vp-mono">{a.id}</span>
              </Field>
              <Field label="Content hash">
                <Hash value={a.contentHash} onCopy={() => store.toast("Content hash copied.")} />
              </Field>
              <Field label="Kind">
                <span className="vp-mono">{a.sourceKind}</span>
              </Field>
              <Field label="Size">{fmtBytes(a.sizeBytes)}</Field>
              <Field label="Observations">{fmtNumber(a.observationCount)}</Field>
              <Field label="State">
                <Badge tone={a.state === "active" ? "pass" : "waived"}>{a.state}</Badge>
              </Field>
              <Field label="At rest">
                <Badge tone="pass" glyph="✓">
                  encrypted
                </Badge>
              </Field>
              <Field label="Collected">{fmtDateTime(a.collectedAt)}</Field>
            </Fields>
          </Card>

          <Card title="Controls this feeds" flush>
            <div className="vp-rows">
              {a.controlIds.map((cid) => {
                const c = control(cid);
                if (!c) return null;
                return (
                  <button
                    key={cid}
                    type="button"
                    className="vp-row"
                    data-clickable="true"
                    onClick={() => onNavigate(href("controls", cid))}
                  >
                    <span className="vp-row-main">
                      <span className="vp-row-title">
                        <span className="vp-mono" style={{ color: "var(--ink-3)", marginRight: "calc(var(--spacing) * 1.5)" }}>
                          {c.ref}
                        </span>
                        {c.family}
                      </span>
                    </span>
                    <span className="vp-row-aside">
                      <Verdict status={c.status} />
                    </span>
                  </button>
                );
              })}
            </div>
          </Card>
        </aside>
      </div>
    </PageShell>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   CONNECTORS
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Connectors.
 *
 * ── Read-only is stated on every row, not once in the marketing ──
 *
 * The scopes granted to each source are shown in full, and the page says plainly
 * that no connector holds a write capability. For a product that asks for
 * credentials to your identity provider and your cloud account, that is the first
 * question a security reviewer asks, and burying the answer in documentation is
 * how a deal stalls.
 *
 * ── The failing sources come first ──
 *
 * Healthy sources need no attention, so the two broken ones are pulled to the top
 * with the CONSEQUENCE named: not "auth error" but "this is why CC6.2 is UNKNOWN".
 * Connecting a source failure to the control it invalidates is the single most
 * useful thing this page can do.
 */
export function ConnectorsPage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const store = useStore();
  const unhealthy = SOURCES.filter((s) => s.health !== "healthy");
  const healthy = SOURCES.filter((s) => s.health === "healthy");

  return (
    <PageShell
      eyebrow="Evidence"
        title="Connectors"
        lede="Read-only connections to the systems that hold the truth. Every connector requests the narrowest scope that satisfies its controls, and none of them can write."
        actions={
          <Button variant="primary" icon="plus" onClick={() => store.toast("Prototype: the connector catalogue would open.")}>
            Add a source
          </Button>
        }
    >

      <Grid cols={4}>
        <Stat
          label="Sources"
          value={SOURCES.length}
          foot={`${healthy.length} healthy · ${unhealthy.length} needing attention`}
        />
        <Stat
          label="Collections in the window"
          value={fmtNumber(SOURCES.reduce((s, x) => s + x.artifactCount, 0))}
          foot="Every snapshot these sources have taken. The Evidence library counts the artifacts currently retained."
        />
        <Stat
          label="Write capabilities held"
          value="0"
          tone="pass"
          foot="Every connector is read-only by construction"
        />
        <Stat
          label="Controls unevidenced"
          value={store.controls.filter((c) => c.sourceIds.length === 0).length}
          tone={store.controls.some((c) => c.sourceIds.length === 0) ? "fail" : "pass"}
          foot="No source mapped, so no check can run"
        />
      </Grid>

      {unhealthy.length > 0 ? (
        <Section
          title="Needs attention"
          note="Listed first, with the control each failure invalidates — a source problem is usually the real cause of an UNKNOWN."
        >
          <div className="vp-stack" data-gap="sm">
            {unhealthy.map((s) => (
              <SourceCard key={s.id} src={s} onNavigate={onNavigate} />
            ))}
          </div>
        </Section>
      ) : null}

      <Section title="Healthy" note="Inside their freshness window, with the scopes they need.">
        <div className="vp-stack" data-gap="sm">
          {healthy.map((s) => (
            <SourceCard key={s.id} src={s} onNavigate={onNavigate} />
          ))}
        </div>
      </Section>
    </PageShell>
  );
}

function SourceCard({ src, onNavigate }: { src: Source; onNavigate: (to: string) => void }) {
  const store = useStore();
  const missing = src.requiredScopes.filter((s) => !src.grantedScopes.includes(s));
  const affected = src.controlIds.map((cid) => control(cid)).filter(Boolean);

  return (
    <Card
      title={src.displayName}
      subtitle={
        <>
          {PROVIDER_LABEL[src.provider]} · {src.family} · every {Math.round(src.cadenceMinutes / 60)}h ·{" "}
          {fmtNumber(src.artifactCount)} artifacts collected
        </>
      }
      actions={
        <>
          <Badge tone="neutral" glyph="🔒">
            read-only
          </Badge>
          <Badge tone={HEALTH_TONE[src.health]} size="lg">
            {src.health}
          </Badge>
        </>
      }
      footer={
        <>
          <span>
            Last collected{" "}
            {src.lastSuccessfulRunAt ? (
              <>
                {fmtRelative(src.lastSuccessfulRunAt)} ({fmtDateTime(src.lastSuccessfulRunAt)})
              </>
            ) : (
              "never"
            )}
            {src.nextRunAt ? ` · next ${fmtRelative(src.nextRunAt)}` : " · no next run scheduled"}
          </span>
          <span style={{ marginLeft: "auto" }}>
            {src.health === "healthy" ? (
              <Button size="sm" variant="ghost" icon="refresh" onClick={() => store.toast(`Collection queued for ${PROVIDER_LABEL[src.provider]}.`)}>
                Collect now
              </Button>
            ) : (
              <Button
                size="sm"
                variant="primary"
                icon="plug"
                onClick={() => store.toast(`Prototype: the ${PROVIDER_LABEL[src.provider]} reconnect flow would open.`)}
              >
                {missing.length > 0 ? "Grant missing scope" : "Reconnect"}
              </Button>
            )}
          </span>
        </>
      }
    >
      {src.note ? (
        <div style={{ marginBottom: "calc(var(--spacing) * 3)" }}>
          <Callout tone={src.health === "blocked" || src.health === "revoked" ? "fail" : "unknown"} icon="alert">
            {src.note}
          </Callout>
        </div>
      ) : null}

      <Fields>
        <Field label="Freshness SLA">
          {Math.round(src.maxFreshnessMinutes / 60)}h ·{" "}
          {src.lastSuccessfulRunAt ? (
            (() => {
              const ageMin = Math.round(
                (NOW.getTime() - new Date(src.lastSuccessfulRunAt).getTime()) /
                  60_000,
              );
              const inside = ageMin <= src.maxFreshnessMinutes;
              return (
                <Badge tone={inside ? "pass" : "fail"} glyph={inside ? "✓" : "✕"}>
                  {inside ? "inside" : "breached"} — {Math.round(ageMin / 60)}h old
                </Badge>
              );
            })()
          ) : (
            <Badge tone="fail" glyph="✕">
              never collected
            </Badge>
          )}
        </Field>
        <Field label="Granted scopes">
          {src.grantedScopes.length === 0 ? (
            <span className="vp-muted">none — the credential is revoked</span>
          ) : (
            <span className="vp-inline">
              {src.grantedScopes.map((s) => (
                <span key={s} className="vp-hash" style={{ cursor: "default" }}>
                  {s}
                </span>
              ))}
            </span>
          )}
        </Field>
        {missing.length > 0 ? (
          <Field label="Missing scopes">
            <span className="vp-inline">
              {missing.map((s) => (
                <Badge key={s} tone="fail" glyph="✕">
                  {s}
                </Badge>
              ))}
            </span>
          </Field>
        ) : null}
        <Field label="Controls it evidences">
          <span className="vp-inline">
            {affected.map((c) => (
              <button
                key={c!.id}
                type="button"
                className="vp-btn"
                data-variant="outline"
                data-size="sm"
                onClick={() => onNavigate(href("controls", c!.id))}
              >
                <span className="vp-mono">{c!.ref}</span>
                <Verdict status={c!.status} />
              </button>
            ))}
            {affected.length === 0 ? <span className="vp-muted">none yet</span> : null}
          </span>
        </Field>
      </Fields>
    </Card>
  );
}
